import * as React from 'react';
import GridLayout, { Layout } from 'react-grid-layout';
import { useLocation } from 'wouter';
import { useIntl } from 'react-intl';
import { SetterOrUpdater, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import localforage from 'localforage';
import { useQuery } from '@tanstack/react-query';
import { CmdLoader } from '@commander-services/gui-components';
import { uniqBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import Widget from '../../OnlinePanel/Widgets/Widget';
import Config from '../../../../config';
import * as WidgetState from '../../../store/recoil/widgets';
import {
  IVehicleOnlinePanelState,
  IOnlinePanelDefaultTemplateChange,
  IOnlinePanelUserTemplateChange,
  IVehicleOnlinePanels,
  IWidgetGraph,
  onlineMapTemplatesAtom,
  isOnlinePanelOpenAtom,
  userOnlinePanelTemplateAtom,
  defaultOnlinePanelTemplateAtom,
  onlinePanelAtom,
  onlinePanelDataAtom,
  lastStatusAtom,
  isLoadingExtraDataForWidgetsAtom,
} from '../../OnlinePanel/OnlinePanelState';
import SvgIconService from '../../../Services/SvgIconService';
import { URL_ONLINE_PANEL } from '../../../router/constants';
import { ITableFilter, setTableFilterAtom } from '../../../store/recoil/TableState';
import useKeyPress, { ESCAPE_KEY } from '../../../hooks/useKeyPress';
import { IMainMenuItem } from '../../MainMenu/interfaces';
import { IOnlinePanelData, IWidgetCards } from './types';
import { WORD_LENGTH } from './constants';
import {
  IVehicleGdprDataAllowed,
  IVehicles,
  IVehiclesData,
  TVehicleStatus,
  VehicleOnlinePanelAnnouncementApiResponse,
} from '../../Vehicles/types';
import EcoDriveWidgetGraph from './EcoDriveWidgetGraph';
import SpeedWidgetGraph from './SpeedWidgetGraph';
import OrdinaryWidgetGraph from './OrdinaryWidgetGraph';
import FuelLevelPercWidgetGraph from './FuelLevelPercWidgetGraph';
import ThrottleWidgetGraph from './ThrottleWidgetGraph';
import FuelLevelLitersWidgetGraph from './FuelLevelLitersWidgetGraph';
import {
  canEditOnlineTemplate,
  getExtraDataForWidgets,
  getGdprPermission,
} from './OnlinePanelService';
import { getOnlinePanelForVehicle } from '../../../Services/VehicleService';
import { IAnnouncement } from '../../RidePlayer/types';
import EvBatteryKwWidgetGraph from './EvBatteryKwWidgetGraph';
import EvBatteryPercWidgetGraph from './EvBatteryPercWidgetGraph';
import AdBlueWidgetGraph from './AdBluePercWidgetGraph';
import {
  adBluePercWidgetGraphAtom,
  ecoDriveWidgetGraphAtom,
  evBatteryKwWidgetGraphAtom,
  evBatteryPercWidgetGraphAtom,
  fuelLevelLitersWidgetGraphAtom,
  fuelLevelPercWidgetGraphAtom,
  isGraphOpenAtom,
  ordinaryWidgetGraphAtom,
  speedWidgetGraphAtom,
  throttleWidgetGraphAtom,
} from '../../Graphs/GraphState';
import { isVehicleGdprDataAllowedAtom, vehiclesAtom } from '../../../store/recoil/vehicles';
import { vehiclesDataAtom } from '../../../store/recoil/vehiclesData';
import { IWidget } from '../../OnlinePanel/Widgets/types';
import { getWidgetsByLayout } from '../../OnlinePanel/Widgets/WidgetService';
import { isBluecoinsOpenAtom } from '../Bluecoins/BluecoinState';
import { isWaypointsOpenAtom } from '../Waypoints/WaypointState';
import { isStreetviewEnabledAtom } from '../OnlineMapState';
import {
  activeMenuIdAtom,
  activeMenuParentIdAtom,
  isSubMenuShownAtom,
  menuItemsAtom,
} from '../../MainMenu/MainMenuState';
import useAnalytics from '../../../hooks/useAnalytics';

const gdprIcon = SvgIconService.shiftingModeInkognito();

const keysToFind = [
  '4_1_ride_overview_105',
  '4_2_ride_overview_106',
  '4_1_refueling_overview_109',
  '2_1_refueling_overview_108',
  '4_2_last_ride_111',
  '4_1_last_ride_110',
];

const reFetchInterval = 30000;

interface IOnlinePanelProps {
  isRidePlayerOpen: number | boolean;
  setIsRidePlayerOpen: SetterOrUpdater<number | boolean>;
}

function OnlinePanel(props: IOnlinePanelProps): JSX.Element {
  const { trackEvent } = useAnalytics();
  const setTableFilter = useSetRecoilState<ITableFilter | null>(setTableFilterAtom);
  const setActiveMenuParent = useSetRecoilState<number>(activeMenuParentIdAtom);
  const setMenuId = useSetRecoilState(activeMenuIdAtom);
  const setSubmenuToggle = useSetRecoilState<boolean>(isSubMenuShownAtom);
  const isVehicleGdprDataAllowed = useRecoilValue<IVehicleGdprDataAllowed>(
    isVehicleGdprDataAllowedAtom
  );
  const [ecoDriveWidgetGraph, setEcoDriveWidgetGraph] = useRecoilState<IWidgetGraph | null>(
    ecoDriveWidgetGraphAtom
  );
  const [speedWidgetGraph, setSpeedWidgetGraph] = useRecoilState<IWidgetGraph | null>(
    speedWidgetGraphAtom
  );
  const [throttleWidgetGraph, setThrottleWidgetGraph] = useRecoilState<IWidgetGraph | null>(
    throttleWidgetGraphAtom
  );
  const setIsGraphOpen = useSetRecoilState<boolean | number>(isGraphOpenAtom);
  const [fuelLevelLitersWidgetGraph, setFuelLevelLitersWidgetGraph] =
    useRecoilState<IWidgetGraph | null>(fuelLevelLitersWidgetGraphAtom);
  const [fuelLevelPercWidgetGraph, setFuelLevelPercWidgetGraph] =
    useRecoilState<IWidgetGraph | null>(fuelLevelPercWidgetGraphAtom);
  const [ordinaryWidgetGraph, setOrdinaryWidgetGraph] = useRecoilState<IWidgetGraph | null>(
    ordinaryWidgetGraphAtom
  );
  const { formatMessage: f } = useIntl();
  const vehicles = useRecoilValue<IVehicles>(vehiclesAtom);
  const vehiclesData = useRecoilValue<IVehiclesData>(vehiclesDataAtom);
  const vehicleId = useRecoilValue<number | false>(isOnlinePanelOpenAtom);
  const [isWaypointsOpen] = useRecoilState<boolean>(isWaypointsOpenAtom);
  const onlinePanelDataRedux = useRecoilValue<IOnlinePanelData[]>(onlineMapTemplatesAtom);
  const usedWidgets = useRecoilValue<IWidget[]>(WidgetState.usedWidgets);
  const userOnlinePanelTemplate = useRecoilValue<IOnlinePanelUserTemplateChange>(
    userOnlinePanelTemplateAtom
  );
  const defaultOnlinePanelTemplate = useRecoilValue<IOnlinePanelDefaultTemplateChange>(
    defaultOnlinePanelTemplateAtom
  );
  const [onlinePanelVehiclesData, setOnlinePanelVehiclesData] =
    useRecoilState<IVehicleOnlinePanels>(onlinePanelAtom);

  const menuItems = useRecoilValue<IMainMenuItem[]>(menuItemsAtom);
  const [onlinePanelData, setOnlinePanelData] = useRecoilState<IOnlinePanelData | null>(
    onlinePanelDataAtom
  );

  const [gridLayout, setGridLayout] = useState<IWidgetCards[]>([]);
  const [isMinimalized, setIsMinimalized] = useState<boolean>(false);
  const [titleForMinimalizedPanel, setTitleForMinimalizedPanel] = useState<string>('');
  const [clickedTabIndex, setClickedTabIndex] = useState<number>(0);
  const [isOnlinePanelOpen, setIsOnlinePanelOpen] = useRecoilState<number | false>(
    isOnlinePanelOpenAtom
  );
  const isBluecoinsOpen = useRecoilValue<boolean>(isBluecoinsOpenAtom);
  const isStreetviewEnabled = useRecoilValue<boolean>(isStreetviewEnabledAtom);
  const [isLoadingExtraDataForWidgets, setIsLoadingExtraDataForWidgets] = useRecoilState<boolean>(
    isLoadingExtraDataForWidgetsAtom
  );
  const [lastStatus, setLastStatus] = useRecoilState<TVehicleStatus>(lastStatusAtom);
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [lastVehicleId, setLastVehicleId] = useState<number>(0);
  const [isOnlinePanelDataLoaded, setIsOnlinePanelDataLoaded] = useState<boolean>(false);
  const [evBatterykWhWidgetGraph, setEvBatterykWhWidgetGraph] = useRecoilState<IWidgetGraph | null>(
    evBatteryKwWidgetGraphAtom
  );
  const [evBatteryPercWidgetGraph, setEvBatteryPercWidgetGraph] =
    useRecoilState<IWidgetGraph | null>(evBatteryPercWidgetGraphAtom);
  const [adBluePercWidgetGraph, setAdBluePercWidgetGraph] = useRecoilState<IWidgetGraph | null>(
    adBluePercWidgetGraphAtom
  );

  const [, navigate] = useLocation();

  useMemo(() => {
    // Skip processing if vehicleId is not a number
    if (typeof vehicleId !== 'number') {
      setGridLayout([]);
      return;
    }

    // Determine which template to use based on conditions
    const selectedTemplate = userOnlinePanelTemplate.vehicleId?.includes(vehicleId)
      ? userOnlinePanelTemplate.template
      : defaultOnlinePanelTemplate.vehicleId?.includes(vehicleId)
        ? defaultOnlinePanelTemplate.template
        : vehiclesData[vehicleId]?.templateId
          ? onlinePanelDataRedux.find(
              (template) => template.id === vehiclesData[vehicleId].templateId
            )
          : null;

    // If a selected template is found, set online panel data and grid layout
    if (selectedTemplate) {
      setOnlinePanelData(selectedTemplate);
      const tempGridLayout = selectedTemplate.cards.map((item) => {
        const wigetsWithoutDuplicates: Layout[] = uniqBy(item.widgets, 'i');
        const newItem = {
          ...item,
          widgets: wigetsWithoutDuplicates.map((widget: Layout) => ({
            ...widget,
            static: true,
            isDraggable: false,
          })),
        };
        return newItem;
      });
      setGridLayout(tempGridLayout);
      if (tempGridLayout[clickedTabIndex]?.widgets) {
        const containsAnyKey = keysToFind.some((key) =>
          tempGridLayout[clickedTabIndex]?.widgets.some((widget) => widget.i === key)
        );

        if (containsAnyKey) {
          setIsLoadingExtraDataForWidgets(true);
        } else {
          setIsLoadingExtraDataForWidgets(false);
        }
      }
    }
  }, [
    vehicleId,
    userOnlinePanelTemplate.vehicleId,
    userOnlinePanelTemplate.template,
    defaultOnlinePanelTemplate.vehicleId,
    defaultOnlinePanelTemplate.template,
    vehiclesData,
    onlinePanelDataRedux,
    clickedTabIndex,
    setOnlinePanelData,
    setIsLoadingExtraDataForWidgets,
  ]);

  useMemo(() => {
    if (isMinimalized) {
      const filteredVehicles = vehicles[vehicleId as number];

      if (filteredVehicles) {
        setTitleForMinimalizedPanel(
          `${filteredVehicles.licenceNumber}${
            filteredVehicles.driver.name ? `, ${filteredVehicles.driver.name}` : ''
          } `
        );
      }
    }
  }, [isMinimalized, vehicleId, vehicles]);

  const handleMinMaxlizePanel = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    if (isMinimalized) {
      trackEvent(['online panel', 'click', `Maximalize online panel for vehicle: ${vehicleId}`]);
      setIsMinimalized(false);
    }
    if (!isMinimalized) {
      trackEvent(['online panel', 'click', `Minimalize online panel for vehicle: ${vehicleId}`]);
      setIsMinimalized(true);
    }
  };

  const handleSelectTab = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    const tabTitle = event.currentTarget.title;
    trackEvent(['online panel', 'click', `Select tab: ${tabTitle}`]);
    setClickedTabIndex(Number(event.currentTarget.id));
  };

  const handleResetGraphs = () => {
    setEcoDriveWidgetGraph(null);
    setSpeedWidgetGraph(null);
    setOrdinaryWidgetGraph(null);
    setFuelLevelPercWidgetGraph(null);
    setFuelLevelLitersWidgetGraph(null);
    setThrottleWidgetGraph(null);
    setEvBatterykWhWidgetGraph(null);
    setEvBatteryPercWidgetGraph(null);
    setAdBluePercWidgetGraph(null);
    localforage.setItem('widgetGraph', null);
    setIsGraphOpen(false);
  };

  const handleLastRide = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): boolean => {
    event.preventDefault();
    trackEvent(['online panel', 'click', `Open last ride player for vehicle: ${vehicleId}`]);
    props.setIsRidePlayerOpen(vehicleId);
    handleResetGraphs();
    return true;
  };

  const handleOnlinePanelSettings = () => {
    trackEvent(['online panel', 'click', 'Open settings for online panel']);
    const filter = { licenceNumber: [String(vehicleId)] };

    setTableFilter(filter);
    navigate(`${URL_ONLINE_PANEL}/assign`);

    let menuTemp: IMainMenuItem | undefined;
    menuItems.forEach((menuItem: IMainMenuItem) => {
      if (menuItem.items && menuItem.items.find((item) => item.link === URL_ONLINE_PANEL)) {
        menuTemp = menuItem.items.find((item) => item.link === URL_ONLINE_PANEL);
        if (menuTemp) {
          setActiveMenuParent(menuItem.id);
          setMenuId(menuTemp.id);
          setSubmenuToggle(true);
        }
      }
    });
  };

  useEffect(() => {
    if (onlinePanelData && onlinePanelData.cards.length <= clickedTabIndex) {
      setClickedTabIndex(0);
    }
  }, [clickedTabIndex, onlinePanelData]);

  const handleCloseOnlinePanel = () => {
    if (isOnlinePanelOpen) {
      trackEvent(['online panel', 'closed', `Vehicle: ${isOnlinePanelOpen}`]);
    }
    setIsOnlinePanelOpen(false);
    handleResetGraphs();
  };

  useEffect(() => {
    if (isOnlinePanelOpen) {
      trackEvent(['online panel', 'open', `Vehicle: ${isOnlinePanelOpen}`]);
    }
    return () => {
      handleResetGraphs();

      // When online panel is closed - close also ride player
      if (props.isRidePlayerOpen) {
        props.setIsRidePlayerOpen(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnlinePanelOpen]);

  useKeyPress(ESCAPE_KEY, () => {
    handleCloseOnlinePanel();
    props.setIsRidePlayerOpen(false);
  });

  const getOnlinePanelDefault = () => {
    if (onlinePanelData) {
      return (
        <GridLayout
          className="b-widget-grid b-widget-grid--rounded"
          key={`gridlayout-${gridLayout[clickedTabIndex].name}-${gridLayout[clickedTabIndex].id}`}
          style={{
            gridTemplateColumns: `repeat(${onlinePanelData?.columnSize}, min-content)`,
            gridTemplateRows: `repeat(${onlinePanelData?.rowSize}, min-content)`,
            minWidth: onlinePanelData.columnSize * 72 + (onlinePanelData.columnSize - 1) * 4,
            minHeight: onlinePanelData.rowSize * 72 + (onlinePanelData.rowSize - 1) * 4,
          }}
          layout={gridLayout[clickedTabIndex].widgets} // add static: true
          cols={onlinePanelData.columnSize}
          maxRows={onlinePanelData.rowSize}
          rowHeight={72}
          width={onlinePanelData.columnSize * 72 + (onlinePanelData.columnSize - 1) * 4}
          isResizable={false}
          isDroppable={false}
          margin={[4, 4]}
        >
          {getWidgetsByLayout(
            usedWidgets, // used widgets
            gridLayout[clickedTabIndex].widgets // template cards
          ).map((widget: IWidget, widgetIndex: number) => (
            <div className="item" key={`${widget.gridName}`} style={{ zIndex: 9999 - widgetIndex }}>
              <Widget
                key={`${widgetIndex}-${widget.gridName}`}
                index={widgetIndex}
                data={widget}
                isMockupMode={false}
                isInGrid={false}
              />
            </div>
          ))}
        </GridLayout>
      );
    }
    return null;
  };

  const getOnlinePanelLayout = () => {
    if (!onlinePanelData) {
      return null;
    }
    if (
      !getGdprPermission(
        onlinePanelVehiclesData,
        vehicleId,
        vehicles,
        isVehicleGdprDataAllowed,
        vehiclesData
      )
    ) {
      return (
        <div className="b-incognito-info">
          <img title={f({ id: 'widget.rideType.tooltip.yes' })} src={gdprIcon} alt="GDPR" />
        </div>
      );
    }
    if (ecoDriveWidgetGraph && ecoDriveWidgetGraph.vehicleId === isOnlinePanelOpen) {
      return <EcoDriveWidgetGraph vehicleId={ecoDriveWidgetGraph.vehicleId} />;
    }
    if (speedWidgetGraph && speedWidgetGraph.vehicleId === isOnlinePanelOpen) {
      return <SpeedWidgetGraph vehicleId={speedWidgetGraph.vehicleId} />;
    }
    if (fuelLevelPercWidgetGraph && fuelLevelPercWidgetGraph.vehicleId === isOnlinePanelOpen) {
      return <FuelLevelPercWidgetGraph vehicleId={fuelLevelPercWidgetGraph.vehicleId} />;
    }
    if (fuelLevelLitersWidgetGraph && fuelLevelLitersWidgetGraph.vehicleId === isOnlinePanelOpen) {
      return <FuelLevelLitersWidgetGraph vehicleId={fuelLevelLitersWidgetGraph.vehicleId} />;
    }
    if (throttleWidgetGraph && throttleWidgetGraph.vehicleId === isOnlinePanelOpen) {
      return <ThrottleWidgetGraph vehicleId={throttleWidgetGraph.vehicleId} />;
    }
    if (ordinaryWidgetGraph && ordinaryWidgetGraph.vehicleId === isOnlinePanelOpen) {
      return (
        <OrdinaryWidgetGraph
          vehicleId={ordinaryWidgetGraph.vehicleId}
          name={ordinaryWidgetGraph.name || ''}
        />
      );
    }
    if (evBatterykWhWidgetGraph && evBatterykWhWidgetGraph.vehicleId === isOnlinePanelOpen) {
      return <EvBatteryKwWidgetGraph vehicleId={evBatterykWhWidgetGraph.vehicleId} />;
    }
    if (evBatteryPercWidgetGraph && evBatteryPercWidgetGraph.vehicleId === isOnlinePanelOpen) {
      return <EvBatteryPercWidgetGraph vehicleId={evBatteryPercWidgetGraph.vehicleId} />;
    }
    if (adBluePercWidgetGraph && adBluePercWidgetGraph.vehicleId === isOnlinePanelOpen) {
      return <AdBlueWidgetGraph vehicleId={adBluePercWidgetGraph.vehicleId} />;
    }
    return getOnlinePanelDefault();
  };

  useEffect(() => {
    if (typeof vehicleId === 'number' && vehicleId !== lastVehicleId) {
      // setIsLoadingExtraDataForWidgets(false);
      setLastVehicleId(vehicleId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLayout, clickedTabIndex]);

  const { isLoading: isOnlinePanelLoading, data: onlinePanelDataApi } = useQuery({
    queryKey: [`onlinePanelData-${vehicleId}`, vehicleId],
    queryFn: () => getOnlinePanelForVehicle(vehicleId),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    enabled:
      typeof vehicleId === 'number' &&
      getGdprPermission(
        onlinePanelVehiclesData,
        vehicleId,
        vehicles,
        isVehicleGdprDataAllowed,
        vehiclesData
      ),
  });

  useEffect(() => {
    if (!isOnlinePanelDataLoaded && onlinePanelDataApi) {
      setOnlinePanelVehiclesData((oldState: IVehicleOnlinePanels) => {
        const ann: IAnnouncement[] = onlinePanelDataApi.announcements.map(
          (item: VehicleOnlinePanelAnnouncementApiResponse) => {
            const newItem: IAnnouncement = {
              id: item.id,
              title: item.title,
              type: item.type,
              activatedAt: item.activatedAt,
              duration: item.duration,
              scope: item.scope,
              userId: item.userId || undefined,
              eventType: item.eventType || undefined,
            };
            return newItem;
          }
        );
        const data: IVehicleOnlinePanelState = {
          vehicleId: Number(vehicleId),
          altitude: onlinePanelDataApi.altitude,
          speed: onlinePanelDataApi.speed,
          battery: onlinePanelDataApi.battery,
          engineTemperature: onlinePanelDataApi.engineTemperature,
          engineState: onlinePanelDataApi.engineState,
          fuelLevelLiters: onlinePanelDataApi.fuelLevelLiters,
          fuelLevelPercentage: onlinePanelDataApi.fuelLevelPercentage,
          rpm: onlinePanelDataApi.rpm,
          satellites: onlinePanelDataApi.satellites,
          throttle: onlinePanelDataApi.throttle,
          ecoDriveScore: onlinePanelDataApi.ecoDriveScore,
          timestamp: new Date().toISOString(),
          // odometerCan: null,
          onCruise: onlinePanelDataApi.onCruise,
          onCng: onlinePanelDataApi.onCng,
          onLpg: onlinePanelDataApi.onLpg,
          latitude: 0,
          longitude: 0,
          remoteLock: onlinePanelDataApi.remoteLock,
          webasto: onlinePanelDataApi.webasto,
          address: onlinePanelDataApi.address,
          ignition: false, // not used
          pto: onlinePanelDataApi.pto,
          code: '',
          announcements: ann,
          mileage: onlinePanelDataApi.mileage,
          electric: onlinePanelDataApi.electric,
          seatbelts: onlinePanelDataApi.seatbelts,
          coolantLevelLow: onlinePanelDataApi.coolantLevelLow,
          oilLevelLow: onlinePanelDataApi.oilLevelLow,
          checkEngine: onlinePanelDataApi.checkEngine,
          esp: onlinePanelDataApi.esp,
          adBlueLevel: onlinePanelDataApi.adBlueLevel,
        };

        const newState = {
          ...oldState,
          [Number(vehicleId)]: data,
        };
        return newState;
      });
      setIsOnlinePanelDataLoaded(true);
    }
  }, [isOnlinePanelDataLoaded, onlinePanelDataApi, setOnlinePanelVehiclesData, vehicleId]);

  const {
    isLoading: isLoadingExtraDataForWidget,
    data,
    refetch,
  } = useQuery({
    queryKey: ['widgetsDataForOnlinePanel', vehicleId],
    queryFn: () => getExtraDataForWidgets(vehicleId),
    staleTime: 9000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    enabled:
      typeof vehicleId === 'number' &&
      isLoadingExtraDataForWidgets &&
      getGdprPermission(
        onlinePanelVehiclesData,
        vehicleId,
        vehicles,
        isVehicleGdprDataAllowed,
        vehiclesData
      ),
  });

  // Effect to detect status change to "STOP"
  useEffect(() => {
    // Check if status changed to "STOP" and refetch hasn't been scheduled yet
    if (
      vehicleId &&
      vehiclesData[vehicleId]?.status === 'STOP' &&
      lastStatus !== 'STOP' &&
      !shouldRefetch
    ) {
      // Schedule refetch
      setShouldRefetch(true);
    }
    // Update lastStatus to currentStatus every time status changes
    if (vehicleId && vehiclesData[vehicleId]?.status !== lastStatus) {
      setLastStatus(vehiclesData[vehicleId]?.status);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, vehiclesData, lastStatus, shouldRefetch]);

  // Effect to handle scheduled refetch
  useEffect(() => {
    if (shouldRefetch) {
      const timer = setTimeout(() => {
        refetch();
        // Reset shouldRefetch to allow future refetches if status changes to "STOP" again
        setShouldRefetch(false);
      }, reFetchInterval);

      // Cleanup function to clear the timeout
      return () => clearTimeout(timer);
    }
    return () => {
      /* no-op */
    };
  }, [shouldRefetch, refetch]);

  return (
    <div className=" w-map w-map--wrapper" data-cy={`online-panel-${vehicleId}`}>
      <div className="w-map">
        {(isLoadingExtraDataForWidget || isOnlinePanelLoading) && <CmdLoader inContent={true} />}
        {isMinimalized && onlinePanelData && !props.isRidePlayerOpen && (
          <div className="b-widgets-info">
            <div
              className="b-tabs-toggler b-tabs-toggler--min"
              style={{ minWidth: onlinePanelData.columnSize * 72 - 8, maxWidth: '34px' }}
            >
              <ul>
                <li className="title">{titleForMinimalizedPanel}</li>
                {/* <li className="tab-actions">
                    <a
                      className="cmd-icon cmd-icon-play-button"
                      href="#"
                      data-cy="open-player-from-minimalized-online-panel"
                    > */}
                {/* &nbsp; */}
                {/* </a>
                  </li> */}

                <li className="tab-actions">
                  <a
                    className="cmd-icon cmd-icon-maximalise-2"
                    href="#"
                    title={f({
                      id: 'online.panel.map.maximalize',
                    })}
                    onClick={handleMinMaxlizePanel}
                  >
                    {/* &nbsp; */}
                  </a>
                </li>

                <li className="tab-actions">
                  <a
                    className="cmd-icon cmd-icon-remove"
                    href="#"
                    title={f({
                      id: 'online.panel.map.close',
                    })}
                    onClick={handleCloseOnlinePanel}
                  >
                    {/* &nbsp; */}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        )}
        {onlinePanelData && onlinePanelData.columnSize && gridLayout && usedWidgets && (
          <div
            className="b-widgets-info"
            style={{
              display:
                isMinimalized || isWaypointsOpen || isBluecoinsOpen || props.isRidePlayerOpen
                  ? 'none'
                  : '',
            }}
          >
            <div className="b-tabs-toggler">
              <ul>
                {onlinePanelData.cards &&
                  onlinePanelData.cards.map((card: IWidgetCards, index: number) => (
                    <li
                      className={clickedTabIndex === index ? 'active' : ''}
                      key={`${card.id}-${card.name}`}
                    >
                      <a
                        id={`${index}`}
                        href="#"
                        onClick={handleSelectTab}
                        data-cy={`clicked-tab-online-panel-${index}`}
                        style={{ width: 'auto' }}
                        title={card.name}
                      >
                        {card.name.length > WORD_LENGTH
                          ? `${card.name.substring(0, WORD_LENGTH)} ...`
                          : card.name}
                      </a>
                    </li>
                  ))}

                <li className="tab-actions">
                  {canEditOnlineTemplate(onlinePanelData) ? (
                    <a
                      id="minimalize_online_panel"
                      className="cmd-icon cmd-icon-cog"
                      href="#"
                      data-cy="open-settings-from-online-panel"
                      title={f({
                        id: 'online.panel.map.settings',
                      })}
                      onClick={handleOnlinePanelSettings}
                    >
                      {/* &nbsp; */}
                    </a>
                  ) : (
                    <a
                      id="minimalize_online_panel"
                      className="cmd-icon cmd-icon-cog"
                      href="#"
                      data-cy="open-settings-from-online-panel"
                      style={{ color: '#ccc', pointerEvents: 'none' }}
                      title={f({
                        id: 'online.panel.map.settings',
                      })}
                    >
                      <span className="sr-only">{f({ id: 'online.panel.map.settings' })}</span>
                    </a>
                  )}
                </li>

                {Config.Player.enableOnOnlineMap &&
                  getGdprPermission(
                    onlinePanelVehiclesData,
                    vehicleId,
                    vehicles,
                    isVehicleGdprDataAllowed,
                    vehiclesData
                  ) &&
                  !isStreetviewEnabled && (
                    <li className="tab-actions">
                      <a
                        className="cmd-icon cmd-icon-play-button"
                        href="#"
                        data-cy="open-player-from-online-panel"
                        onClick={handleLastRide}
                        title={f({ id: 'rides.lastRide.tooltip' })}
                      >
                        {/* &nbsp; */}
                      </a>
                    </li>
                  )}

                <li className="tab-actions">
                  <a
                    className="cmd-icon cmd-icon-minimalise"
                    href="#"
                    title={f({
                      id: 'online.panel.map.minimalize',
                    })}
                    onClick={handleMinMaxlizePanel}
                    data-cy="minimalize-online-panel"
                  >
                    {/* &nbsp; */}
                  </a>
                </li>

                <li className="tab-actions">
                  <a
                    className="cmd-icon cmd-icon-remove"
                    href="#"
                    title={f({
                      id: 'online.panel.map.close',
                    })}
                    onClick={handleCloseOnlinePanel}
                    data-cy="close-online-panel"
                  >
                    {/* &nbsp; */}
                  </a>
                </li>
              </ul>
            </div>

            <div className="b-widgets-info-tab-content">
              {gridLayout[clickedTabIndex] && getOnlinePanelLayout()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default React.memo(OnlinePanel);
